import { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { View, Text, StyleSheet, FlatList, RefreshControl, TouchableOpacity } from 'react-native';
import { spacing, typography } from '../../theme';
import { useColors, ThemeColors } from '../../hooks/useColors';
import type { Message } from '../../types/chat';
import { useAuth } from '../../hooks/useAuth';
import { Feather } from '@expo/vector-icons';

const formatDateHeader = (date: Date) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (messageDate.getTime() === today.getTime()) {
    return 'Today';
  }
  if (messageDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }
  
  const options: Intl.DateTimeFormatOptions = { 
    month: 'long', 
    day: 'numeric',
    year: messageDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
  };
  return messageDate.toLocaleDateString(undefined, options);
};

interface ChatMessagesProps {
  messages?: Message[];
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export function ChatMessages({ messages = [], onRefresh, isRefreshing = false }: ChatMessagesProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const { user } = useAuth();
  const flatListRef = useRef<FlatList>(null);
  const [hasScrolledUp, setHasScrolledUp] = useState(false);
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const prevLengthRef = useRef(0);

  const renderMessage = ({ item: message, index }: { item: Message, index: number }) => {
    try {
      const isOwnMessage = message.sender_id === user?.id;
      
      // Check if we need to show a date header
      const showDateHeader = index === 0 || (() => {
        const prevMessage = sortedMessages[index - 1];
        const currentDate = new Date(message.created_at);
        const prevDate = new Date(prevMessage.created_at);
        return currentDate.toDateString() !== prevDate.toDateString();
      })();

      // Check if this message is part of a group from the same sender
      const isLastInGroup = index === sortedMessages.length - 1 || 
        sortedMessages[index + 1].sender_id !== message.sender_id;

      return (
        <View style={styles.messageWrapper}>
          {showDateHeader && (
            <View style={styles.dateHeader}>
              <View style={styles.dateLine} />
              <Text style={styles.dateHeaderText}>
                {formatDateHeader(new Date(message.created_at))}
              </Text>
              <View style={styles.dateLine} />
            </View>
          )}
          
          <View style={[
            styles.messageContainer,
            isOwnMessage ? styles.ownMessage : styles.otherMessage,
            !isLastInGroup && { marginBottom: 4 }
          ]}>
            <Text style={[
              styles.messageText,
              isOwnMessage ? styles.ownMessageText : styles.otherMessageText
            ]}>
              {message.text || ''}
            </Text>
            <Text style={[
              styles.timestamp,
              isOwnMessage ? styles.ownTimestamp : styles.otherTimestamp
            ]}>
              {message.created_at 
                ? new Date(message.created_at).toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })
                : ''
              }
            </Text>
          </View>
        </View>
      );
    } catch (error) {
      console.error('Error rendering message:', error);
      return (
        <View style={[styles.messageContainer, styles.otherMessage]}>
          <Text style={styles.otherMessageText}>Message could not be displayed</Text>
        </View>
      );
    }
  };

  // Sort messages by timestamp, oldest to newest with validation
  const sortedMessages = useMemo(() => {
    if (!Array.isArray(messages)) return [];
    
    return [...messages]
      .filter(msg => msg?.id && msg?.created_at) // Filter out invalid messages
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }, [messages]);

  // Auto scroll to bottom when new messages arrive (only if user hasn't scrolled up)
  useEffect(() => {
    if (sortedMessages.length > prevLengthRef.current) {
      if (hasScrolledUp) {
        // User has scrolled up, increment new messages count
        setNewMessagesCount(prev => prev + (sortedMessages.length - prevLengthRef.current));
      } else {
        // User is at bottom, scroll to new message
        const timer = setTimeout(() => {
          flatListRef.current?.scrollToEnd({ animated: true });
        }, 100);
        setNewMessagesCount(0);
        return () => clearTimeout(timer);
      }
    }
    
    prevLengthRef.current = sortedMessages.length;
  }, [sortedMessages.length, hasScrolledUp]);

  // Handle scroll events to detect if user scrolled up
  const handleScroll = useCallback((event: any) => {
    const { contentOffset, layoutMeasurement, contentSize } = event.nativeEvent;
    const threshold = 100; // pixels from bottom
    const isNearBottom = contentOffset.y + layoutMeasurement.height >= contentSize.height - threshold;
    const wasScrolledUp = hasScrolledUp;
    setHasScrolledUp(!isNearBottom);
    
    // Reset new messages count when user scrolls to bottom
    if (wasScrolledUp && isNearBottom) {
      setNewMessagesCount(0);
    }
  }, [hasScrolledUp]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
    }
  }, [onRefresh]);

  // Scroll to bottom function
  const scrollToBottom = useCallback(() => {
    flatListRef.current?.scrollToEnd({ animated: true });
    setNewMessagesCount(0);
  }, []);

  return (
    <View style={styles.wrapper}>
      <FlatList
        data={sortedMessages}
        renderItem={renderMessage}
        keyExtractor={(message) => `message-${message.id}`}
        contentContainerStyle={styles.container}
        inverted={false}
        removeClippedSubviews={true}
        maxToRenderPerBatch={15}
        windowSize={7}
        initialNumToRender={30}
        ref={flatListRef}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
            title="Pull to refresh messages"
          />
        }
        onContentSizeChange={() => {
          if (!hasScrolledUp) {
            flatListRef.current?.scrollToEnd({ animated: true });
          }
        }}
      />
      
      {/* New messages indicator */}
      {hasScrolledUp && newMessagesCount > 0 && (
        <TouchableOpacity 
          style={styles.newMessagesIndicator}
          onPress={scrollToBottom}
        >
          <Feather name="arrow-down" size={16} color={colors.surface} />
          <Text style={styles.newMessagesText}>
            {newMessagesCount} new message{newMessagesCount > 1 ? 's' : ''}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  wrapper: {
    flex: 1,
    position: 'relative',
    backgroundColor: colors.background, // Can add a subtle pattern or color here
  },
  container: {
    flexGrow: 1,
    padding: spacing.md,
    paddingTop: spacing.lg,
  },
  messageWrapper: {
    width: '100%',
  },
  dateHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: spacing.lg,
    paddingHorizontal: spacing.xl,
  },
  dateLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.border,
    opacity: 0.5,
  },
  dateHeaderText: {
    ...typography.caption,
    paddingHorizontal: spacing.md,
    color: colors.textLight,
    fontWeight: '600',
    fontSize: 12,
  },
  messageContainer: {
    maxWidth: '82%',
    marginBottom: spacing.md,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    // Add subtle shadow for depth
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  ownMessage: {
    alignSelf: 'flex-end',
    backgroundColor: colors.primary,
    borderBottomRightRadius: 4,
  },
  otherMessage: {
    alignSelf: 'flex-start',
    backgroundColor: colors.surface,
    borderBottomLeftRadius: 4,
    borderWidth: 1,
    borderColor: colors.border,
  },
  messageText: {
    fontSize: 15,
    lineHeight: 20,
    marginBottom: 4,
  },
  ownMessageText: {
    color: '#000',
    fontWeight: '400',
  },
  otherMessageText: {
    color: colors.text,
  },
  timestamp: {
    fontSize: 10,
    alignSelf: 'flex-end',
    opacity: 0.5,
  },
  ownTimestamp: {
    color: 'rgba(0,0,0,0.5)',
  },
  otherTimestamp: {
    color: colors.textLight,
  },
  newMessagesIndicator: {
    position: 'absolute',
    bottom: 20,
    alignSelf: 'center',
    backgroundColor: colors.primary,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 20,
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    gap: spacing.xs,
  },
  newMessagesText: {
    color: colors.surface,
    fontSize: 14,
    fontWeight: '600',
  },
}); 